// Định nghĩa cột bảng danh sách bài đăng kênh cộng đồng — tách riêng để
// giữ community-post-list.js gọn (pattern giống Comment/components/comment-columns.js).
import React from "react";
import { Button, Space, Tag, Switch, Popconfirm, Image, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faThumbsUp, faComment, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import { audienceColumn } from "../../../components/audience-fields";
import { renderDiscountTags } from "./community-discount-tags";
import { pushStatsColumn } from "./community-push-stats-column";

const API_IMG_URL = process.env.REACT_APP_API_PUBLIC_URL || "";
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || "";
const FALLBACK_IMG =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==";

const DAY_MS = 24 * 60 * 60 * 1000;

// Cắt nội dung tối đa 2 dòng để mọi hàng cao đều nhau
const TWO_LINE_CLAMP = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    whiteSpace: "normal",
};

export function buildCommunityPostColumns({ onTogglePinned, onEdit, onDelete, hideDays = 0 }) {
    // Mốc tự ẩn khỏi feed (khớp backend community-post-hide-days-service): bài
    // KHÔNG ghim, KHÔNG phải của admin, đăng trước mốc này đang bị ẩn
    const hideCutoff = hideDays > 0 ? Date.now() - hideDays * DAY_MS : null;
    const isAutoHidden = (r) =>
        hideCutoff !== null &&
        !(r.pinned === 1 || r.pinned === true) &&
        r.author?.role !== "admin" &&
        r.createTime < hideCutoff;

    return [
        {
            title: "Ảnh",
            dataIndex: "images",
            key: "images",
            width: 90,
            render: (images) =>
                images && images[0] ? (
                    <Image
                        src={`${API_IMG_URL}/img/post-thumbs/${images[0]}`}
                        alt="thumb"
                        width={80}
                        height={45}
                        style={{ objectFit: "cover", borderRadius: 6 }}
                        fallback={FALLBACK_IMG}
                    />
                ) : (
                    <span className="text-content-3">—</span>
                ),
        },
        {
            title: "Nội dung",
            dataIndex: "title",
            key: "title",
            width: 340,
            render: (title, record) => (
                // Bài không có tiêu đề riêng thì title = 80 ký tự đầu của summary
                // -> hiện 1 dòng in đậm + 1 dòng mờ là lặp lại; chỉ để 1 khối 2 dòng.
                <div className="font-semibold" title={record.summary || title} style={TWO_LINE_CLAMP}>
                    {title || record.summary || "—"}
                </div>
            ),
        },
        {
            title: "Mã giảm giá",
            dataIndex: "discountCode",
            key: "discountCode",
            width: 160,
            // Nhiều mã ngăn bởi dấu phẩy -> 3 tag đầu + "+N" tooltip
            render: renderDiscountTags,
        },
        {
            title: "Danh mục",
            dataIndex: "category",
            key: "category",
            width: 130,
            render: (category) => <Tag color="blue">#{category}</Tag>,
        },
        audienceColumn(),
        pushStatsColumn(),
        {
            title: "Tác giả",
            dataIndex: "author",
            key: "author",
            width: 130,
            render: (author) => author?.name || "—",
        },
        {
            title: "Tương tác",
            key: "interaction",
            width: 110,
            render: (_, record) => (
                <Space size={10}>
                    <span title="Lượt thích">
                        <FontAwesomeIcon icon={faThumbsUp} className="text-blue-400 mr-1" />
                        {record.likeCount || 0}
                    </span>
                    <span title="Bình luận">
                        <FontAwesomeIcon icon={faComment} className="text-content-3 mr-1" />
                        {record.commentCount || 0}
                    </span>
                </Space>
            ),
        },
        {
            title: "Ghim",
            dataIndex: "pinned",
            key: "pinned",
            width: 70,
            render: (pinned, record) => (
                <Switch checked={pinned === 1 || pinned === true} onChange={(checked) => onTogglePinned(record, checked)} />
            ),
        },
        {
            title: "Ngày đăng",
            dataIndex: "createTime",
            key: "createTime",
            width: 130,
            render: (ts, record) => (
                <div>
                    {ts ? format(new Date(ts), "dd/MM/yy HH:mm") : "—"}
                    {isAutoHidden(record) && (
                        <Tooltip title={`Đăng quá ${hideDays} ngày — đã tự ẩn với người dùng (admin/mod vẫn xem được)`}>
                            <div>
                                <Tag color="default" className="mt-1">Đã ẩn</Tag>
                            </div>
                        </Tooltip>
                    )}
                </div>
            ),
        },
        {
            title: "Hành động",
            key: "action",
            width: 120,
            fixed: "right",
            render: (_, record) => (
                <Space size={4}>
                    <Tooltip title="Xem deal trên web">
                        <Button
                            size="small"
                            icon={<FontAwesomeIcon icon={faExternalLinkAlt} />}
                            onClick={() => window.open(`${FRONTEND_URL}/cong-dong/${record.postId}`, "_blank")}
                        />
                    </Tooltip>
                    <Tooltip title="Sửa">
                        <Button size="small" icon={<FontAwesomeIcon icon={faEdit} />} onClick={() => onEdit(record)} />
                    </Tooltip>
                    <Popconfirm
                        title="Xóa bài đăng?"
                        description="Bài sẽ bị ẩn khỏi kênh cộng đồng. Bạn có chắc không?"
                        okText="Xóa"
                        cancelText="Hủy"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => onDelete(record.postId)}
                    >
                        <Tooltip title="Xóa">
                            <Button size="small" danger icon={<FontAwesomeIcon icon={faTrash} />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];
}
